import { RefinementCtx, z } from 'zod';
import {
  DonorProfileFormValues,
  OrganSpecificDonorProfileFormValues,
  SharedDonorProfileFormValues,
} from '@features/forms/DonorProfileForm/types';
import { isValidUrl } from '@features/forms/CaseForms/validation.utils';
import { MAX_FILES_AMOUNT } from '@features/config/files/files.config';
import { BloodGroupAbo, CauseOfDeath, DonorGender, DonorType, NeuroStatus, OrganType } from '@/api/apis/case';
import { TMDXFile } from '@/api/apis/files';
import { addCustomZodErrors } from '@/utils/helpers';

type RequiredFieldRule = { field: keyof DonorProfileFormValues; message: string };

const addDonorDataRequiredFieldIssues = (
  formData: DonorProfileFormValues,
  ctx: RefinementCtx,
  rules: RequiredFieldRule[],
) => {
  rules.forEach(({ field, message }) => {
    const value = formData[field];

    if (value === null || value === undefined || value === '') {
      addCustomZodErrors({ ctx, path: [field], message });
    }
  });
};

const DEMOGRAPHICS_REQUIRED_FIELDS: RequiredFieldRule[] = [
  { field: 'donorAge', message: 'Enter Age' },
  { field: 'donorWeight', message: 'Enter Weight' },
  { field: 'donorHeight', message: 'Enter Height' },
  { field: 'donorGender', message: 'Select Gender' },
];

const ADDITIONAL_INFO_REQUIRED_FIELDS: RequiredFieldRule[] = [
  { field: 'reasonForUnallocated', message: 'Enter Reason for Unallocated' },
];

/** Adding an organ never adds a required field — the mandatory set is all shared. */
export const validateSharedDonorProfileRequiredFields = (
  formData: SharedDonorProfileFormValues,
  ctx: RefinementCtx,
) => {
  addDonorDataRequiredFieldIssues(formData, ctx, DEMOGRAPHICS_REQUIRED_FIELDS);
  addDonorDataRequiredFieldIssues(formData, ctx, ADDITIONAL_INFO_REQUIRED_FIELDS);
};

/** `path` so this works flat or nested under an organ. */
export const validateCtScanLinkFormat = (
  ctScanLink: OrganSpecificDonorProfileFormValues['ctScanLink'],
  ctx: RefinementCtx,
  path: (string | number)[],
) => {
  const ctScanUrl = ctScanLink?.[0]?.url;
  if (ctScanUrl && !isValidUrl(ctScanUrl)) {
    addCustomZodErrors({ ctx, path: [...path, 'ctScanLink', 0, 'url'], message: 'Incorrect URL format' });
  }
};

export const validateDonorProfileRequiredFields = (
  formData: { organType: OrganType; donorType: DonorType } & DonorProfileFormValues,
  ctx: RefinementCtx,
) => {
  validateSharedDonorProfileRequiredFields(formData, ctx);
  validateCtScanLinkFormat(formData.ctScanLink, ctx, []);
};

const positiveDecimalField = () =>
  z
    .string()
    .nullish()
    .refine((val) => !val || (/^\d*\.?\d+$/.test(val) && Number(val) >= 0), {
      message: 'Enter a valid positive number',
    });

const integerField = () =>
  z
    .string()
    .nullish()
    .refine((val) => !val || /^-?\d+$/.test(val), {
      message: 'Enter a valid integer',
    });

const positiveIntegerField = () =>
  z
    .string()
    .nullish()
    .refine((val) => !val || (/^\d+$/.test(val) && Number(val) >= 0), {
      message: 'Enter a valid positive integer',
    });

export const sharedDonorProfileSchemaFields = {
  // Required only for DCD donors. DBD donors should not show this field.
  neuroStatus: z.enum(NeuroStatus).nullish(),

  vasopressors: z.boolean().nullish(),
  vasopressorsDetails: z.string().max(512).nullish(),
  sustainedHypotension: z.boolean().nullish(),
  sustainedHypotensionDetails: z.string().max(512).nullish(),
  aboType: z.enum(BloodGroupAbo).nullish(),
  positiveSerologies: z.string().nullish(),
  donorAge: positiveIntegerField(),
  donorWeight: positiveDecimalField(),
  donorHeight: positiveDecimalField(),
  donorBmi: z.string().nullish(),
  donorGender: z.enum(DonorGender).nullish(),
  causeOfDeath: z.enum(CauseOfDeath).nullish(),
  reasonForUnallocated: z.string().max(128).nullish(),
} as const;

export const organSpecificDonorProfileSchemaFields = {
  matchId: z
    .string()
    .nullish()
    .refine((value) => !value || /^\d{7,8}$/.test(value), { message: 'Match ID must be 7-8 digits' }),
  ocsMatchRunCenters: z.string().max(128).nullish(),
  tbiliResult1: positiveDecimalField(),
  tbiliResult2: positiveDecimalField(),
  sgotAstResult1: positiveDecimalField(),
  sgotAstResult2: positiveDecimalField(),
  sgptAltResult1: positiveDecimalField(),
  sgptAltResult2: positiveDecimalField(),
  localSurgeonToProcure: z.boolean().nullish(),
  localSurgeonToProcureDetails: z.string().max(512).nullish(),
  ctScan: z.boolean().nullish(),
  ctScanDetails: z.string().max(512).nullish(),
  // Format validation (when provided) is handled in superRefine.
  ctScanLink: z
    .array(z.object({ id: z.string(), url: z.string(), description: z.string() }))
    .max(1)
    .optional(),
  ctScanDocuments: z.array(z.custom<TMDXFile>()).max(MAX_FILES_AMOUNT).optional(),
  sedation: z.string().max(512).nullish(),
  pfRatio: positiveDecimalField(),
  fluidBalance24hr: integerField(),
  cultureSensitivities: z.string().max(128).nullish(),
} as const;

export const donorProfileSchemaFields = {
  ...sharedDonorProfileSchemaFields,
  ...organSpecificDonorProfileSchemaFields,
} as const;
